/**
 * OAuth Handler — pure authentication logic with zero UI coupling.
 *
 * Responsibilities:
 *  1. Parse OAuth / testing params from the URL
 *  2. Exchange an authorization code for an access token
 *  3. Restore a persisted session from storage
 *  4. Persist a new session to storage
 *
 * Replaces the implicit side-effect logic that was embedded inside
 * `usePartnerAuth`, `useTokenStorage`, and `useAuthApi`.
 */

import { ACCESS_TOKEN_KEY } from "@be-miniapp/app-configs"
import { log } from "@be-miniapp/logging"
import { getPlatform } from "@be-miniapp/platform"
import { generateRandomDeviceID } from "@be-miniapp/shared/utils/misc"

import { normalizeAccessToken, requireAccessToken } from "./auth-token"
import {
  runAuthApiSpan,
  summarizeSearchParams,
} from "./auth-instrumentation"
import type { AuthenticationParams, AuthSession, AuthToken } from "./auth-types"

// ---------------------------------------------------------------------------
// 1. Parse authentication params from URL
// ---------------------------------------------------------------------------

function parseAuthenticationParams(
  searchParams: URLSearchParams
): AuthenticationParams {
  const providerError = searchParams.get("error")
  if (providerError) {
    return {
      kind: "provider-error",
      params: {
        error: providerError,
        description: searchParams.get("error_description") ?? undefined,
      },
    }
  }

  // Fresh partner OAuth params take priority over persisted local sessions.
  const code = searchParams.get("code")
  const codeVerifier = searchParams.get("code_verifier")
  if (code && codeVerifier) {
    return {
      kind: "oauth",
      params: { code, codeVerifier },
    }
  }

  // OAuth code-only (no PKCE)
  if (code) {
    return {
      kind: "oauth",
      params: { code },
    }
  }

  if (searchParams.get("persisted") === "true") {
    return { kind: "persisted" }
  }

  // Internal testing flow
  const phoneNo = searchParams.get("phone_no")
  if (phoneNo) {
    return {
      kind: "internal-testing",
      params: {
        partnerId: Number(searchParams.get("partner_id") ?? 1),
        partnerSecret: searchParams.get("partner_secret") ?? "test",
        phoneNo,
        fullname: searchParams.get("fullname") ?? "VTM",
      },
    }
  }

  return { kind: "none" }
}

// ---------------------------------------------------------------------------
// 2. Exchange authorization code for token (delegates to platform adapter)
// ---------------------------------------------------------------------------

async function exchangeAuthorizationCode(): Promise<AuthToken> {
  const authMethod = getPlatform().auth.authenticateFromParams
  if (!authMethod) {
    throw new Error("authenticateFromParams is not supported on this platform")
  }

  return runAuthApiSpan(
    "api.exchange_access_token",
    summarizeSearchParams(new URLSearchParams(window.location.search)),
    async () => {
      const result = await authMethod()

      return {
        accessToken: requireAccessToken(result.accessToken),
        deviceId: result.deviceId ?? generateRandomDeviceID(),
      }
    }
  )
}

// ---------------------------------------------------------------------------
// 3. Restore persisted session from localStorage
// ---------------------------------------------------------------------------

function restorePersistedSession(): AuthToken | null {
  try {
    const storedToken = normalizeAccessToken(
      localStorage.getItem(ACCESS_TOKEN_KEY)
    )
    if (!storedToken || storedToken.length < 10) return null

    return {
      accessToken: storedToken,
      deviceId: generateRandomDeviceID(),
    }
  } catch (error) {
    log({
      category: "auth",
      level: "error",
      message: "Failed to restore persisted session",
      data: { error },
    })
    return null
  }
}

// ---------------------------------------------------------------------------
// 4. Persist session to localStorage
// ---------------------------------------------------------------------------

function persistSession(token: AuthToken): void {
  try {
    const accessToken = normalizeAccessToken(token.accessToken)
    if (!accessToken) return

    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  } catch (error) {
    log({
      category: "auth",
      level: "error",
      message: "Failed to persist session",
      data: { error },
    })
  }
}

// ---------------------------------------------------------------------------
// 5. Clear persisted session
// ---------------------------------------------------------------------------

function clearPersistedSession(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
  } catch {
    // Silently fail — localStorage may be unavailable
  }
}

// ---------------------------------------------------------------------------
// 6. Build AuthSession from token + partner info
// ---------------------------------------------------------------------------

function buildSession(
  token: AuthToken,
  partnerIdentifier: string
): AuthSession {
  return {
    token,
    partnerIdentifier,
  }
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
  parseAuthenticationParams,
  exchangeAuthorizationCode,
  restorePersistedSession,
  persistSession,
  clearPersistedSession,
  buildSession,
}
